import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { CurrencyItem } from './components/CurrencyItem'
import { SelectDisplayCurrency } from './components/SelectDisplayCurrency'
import { SkeletonListTemplate } from './components/SkeletonListTemplate'
import { WalletCurrency } from '@/domain'

// Mock currency data - будем заменять на реальные API данные позже
const mockCurrencies: WalletCurrency[] = [
  {
    id: '1',
    code: 'USDT',
    name: 'Tether',
    amount: 0,
    amountWithoutNumberConversion: '0',
    icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
    label: '$',
    fiatAmount: 0,
    fiatCurrency: 'USD',
    percentageChange24h: 0,
    revenue24hFiatAmount: 0,
    pricePerUnit: 1,
    ceilingValueForValidation: 10000,
    availableOnRampPaymentMethods: ['card'],
    formattedFiatBalance: '0',
    formattedAmountBalance: '0 USDT',
    formattedUSDBalance: '0',
    formattedStablecoinBalance: '0 USDT',
    usdBalance: 0,
    displayCurrencyCode: 'USDT',
    symbol: '$',
    avatarBGRefColor: '#33CC67',
  },
  {
    id: '2',
    code: 'BTC',
    name: 'Bitcoin',
    amount: 0,
    amountWithoutNumberConversion: '0',
    icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/btc.png',
    label: '₿',
    fiatAmount: 0,
    fiatCurrency: 'USD',
    percentageChange24h: 0,
    revenue24hFiatAmount: 0,
    pricePerUnit: 0,
    ceilingValueForValidation: 10000,
    availableOnRampPaymentMethods: ['card'],
    formattedFiatBalance: '0',
    formattedAmountBalance: '0 BTC',
    formattedUSDBalance: '0',
    formattedStablecoinBalance: '0 BTC',
    usdBalance: 0,
    displayCurrencyCode: 'BTC',
    symbol: '₿',
    avatarBGRefColor: '#F7931A',
  },
  {
    id: '3',
    code: 'ETH',
    name: 'Ethereum',
    amount: 0,
    amountWithoutNumberConversion: '0',
    icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/eth.png',
    label: 'ETH',
    fiatAmount: 0,
    fiatCurrency: 'USD',
    percentageChange24h: 0,
    revenue24hFiatAmount: 0,
    pricePerUnit: 0,
    ceilingValueForValidation: 10000,
    availableOnRampPaymentMethods: ['card'],
    formattedFiatBalance: '0',
    formattedAmountBalance: '0 ETH',
    formattedUSDBalance: '0',
    formattedStablecoinBalance: '0 ETH',
    usdBalance: 0,
    displayCurrencyCode: 'ETH',
    symbol: 'ETH',
    avatarBGRefColor: '#627EEA',
  },
  {
    id: '4',
    code: 'XAUT',
    name: 'Gold',
    amount: 0,
    amountWithoutNumberConversion: '0',
    icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/gold.png',
    label: 'GOLD',
    fiatAmount: 0,
    fiatCurrency: 'USD',
    percentageChange24h: 0,
    revenue24hFiatAmount: 0,
    pricePerUnit: 0,
    ceilingValueForValidation: 10000,
    availableOnRampPaymentMethods: ['card'],
    formattedFiatBalance: '0',
    formattedAmountBalance: '0 XAUT',
    formattedUSDBalance: '0',
    formattedStablecoinBalance: '0 XAUT',
    usdBalance: 0,
    displayCurrencyCode: 'XAUT',
    symbol: 'GOLD',
    avatarBGRefColor: '#DE8637',
  },
]

interface CurrenciesListProps {
  skeletonActive?: boolean
  hasErrorOnLoadingCurrencies?: boolean
  currencies?: WalletCurrency[]
  containerStyle?: React.CSSProperties
  currencyCode?: string
}

export const CurrenciesList: React.FC<CurrenciesListProps> = ({
  skeletonActive = false,
  hasErrorOnLoadingCurrencies = false,
  currencies = [],
  currencyCode,
  containerStyle,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  if (skeletonActive) {
    return (
      <SkeletonListTemplate
        sizeList={3}
        containerStyles={{ paddingLeft: '12px' }}
      />
    )
  }

  // Use real data or fallback to mock data
  const currencyData = currencies.length > 0 ? currencies : mockCurrencies

  // Separate stable assets from crypto (matching mobile's subtype-based grouping)
  const stableAssets = currencyData.filter(
    (currency) =>
      currency.code === 'USDT' ||
      currency.code === 'KWDK' ||
      currency.code === 'AEDK' ||
      currency.code === 'SARK',
  )
  const cryptoAssets = currencyData.filter(
    (currency) =>
      currency.code !== 'USDT' &&
      currency.code !== 'KWDK' &&
      currency.code !== 'AEDK' &&
      currency.code !== 'SARK',
  )

  const sections = [
    {
      title: t('screens:home.stableAssets', { defaultValue: 'Stable Assets' }),
      data: stableAssets,
      marginTop: 0,
    },
    {
      title: t('screens:home.crypto', { defaultValue: 'Crypto' }),
      data: cryptoAssets,
      marginTop: 24,
    },
  ]

  const handleCurrencyPress = (currency: WalletCurrency) => {
    navigate('/currency-details', {
      state: { ...currency },
    })
  }

  return (
    <BaseView style={containerStyle}>
      {/* Показываем секции валют всегда */}
      {sections.map((section) => (
        <BaseView
          key={section.title}
          style={{ marginTop: `${section.marginTop}px` }}
        >
          {/* Section Header */}
          <BaseView px={12} pb={12}>
            <BaseText
              fontSize={16}
              fontFamily="medium"
              color="label.default"
            >
              {section.title}
            </BaseText>
          </BaseView>

          {/* Section Items */}
          <BaseView data-testid="currencies-list" gap={12}>
            {section.data.length === 0 && hasErrorOnLoadingCurrencies ? (
              /* Логируем ошибку, но не показываем в UI */
              <>
                {/* Показываем пустую секцию без сообщения об ошибке */}
                <></>
              </>
            ) : (
              section.data.map((currency) => (
                <CurrencyItem
                  key={currency.id}
                  {...currency}
                  displayCurrencyCode={currencyCode || 'USD'}
                  onPress={handleCurrencyPress}
                />
              ))
            )}
          </BaseView>
        </BaseView>
      ))}

      {/* Footer with Select Display Currency - всегда показываем */}
      <BaseView pt={12}>
        <SelectDisplayCurrency
          hasErrorOnLoadingCurrencies={hasErrorOnLoadingCurrencies}
        />
      </BaseView>
    </BaseView>
  )
}
